import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import AirportSearch from './AirportSearch';

const CABINS = [
  { value: 'economy', label: 'Economy' },
  { value: 'business', label: 'Business' },
  { value: 'firstClass', label: 'First' },
];

const Icon = {
  takeoff: (
    <svg className="w-5 h-5 text-slate-400 mr-2.5 shrink-0" fill="none" stroke="currentColor" strokeWidth={1.8} viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" d="M3 20h18M5.5 15.5l13-5.2c.9-.4 1.4-1.4 1-2.3-.4-.9-1.4-1.3-2.3-1L13 8.6 7.5 5 5.8 5.7l3.4 4.2-3.6 1.4-2-1.2-1.3.5 1.8 3.3c.3.6 1 .8 1.4.6z" />
    </svg>
  ),
  landing: (
    <svg className="w-5 h-5 text-slate-400 mr-2.5 shrink-0" fill="none" stroke="currentColor" strokeWidth={1.8} viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" d="M3 20h18M4.3 9.7l13.2 4.6c.9.3 1.9-.2 2.2-1.1.3-.9-.2-1.9-1.1-2.2l-4.2-1.4-2.6-6.1-1.8-.6.7 5.3-3.7-1.2-1-2.1-1.3-.5-.4 3.8c0 .7.4 1.3 1 1.5z" />
    </svg>
  ),
  calendar: (
    <svg className="w-5 h-5 text-slate-400 mr-2.5 shrink-0" fill="none" stroke="currentColor" strokeWidth={1.8} viewBox="0 0 24 24">
      <rect x="3.5" y="5" width="17" height="15" rx="2" /><path strokeLinecap="round" d="M3.5 10h17M8 3v4M16 3v4" />
    </svg>
  ),
  user: (
    <svg className="w-5 h-5 text-slate-400 mr-2.5 shrink-0" fill="none" stroke="currentColor" strokeWidth={1.8} viewBox="0 0 24 24">
      <circle cx="12" cy="8" r="3.5" /><path strokeLinecap="round" d="M5 20c.8-3.5 3.6-5.5 7-5.5s6.2 2 7 5.5" />
    </svg>
  ),
};

/* One labelled cell of the search bar */
function Field({ label, children, className = '' }) {
  return (
    <div className={`relative bg-white px-4 pt-2.5 pb-3 rounded-xl border border-slate-200 lg:border-0 lg:rounded-none hover:bg-slate-50 focus-within:bg-white focus-within:ring-2 focus-within:ring-blue-500 focus-within:z-20 lg:focus-within:rounded-xl transition-colors ${className}`}>
      <span className="block text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-1">{label}</span>
      {children}
    </div>
  );
}

function Counter({ label, sub, value, min, max, onChange }) {
  const btn = 'w-9 h-9 rounded-full border border-slate-300 text-slate-700 flex items-center justify-center text-lg leading-none hover:border-blue-600 hover:text-blue-600 disabled:opacity-30 disabled:cursor-not-allowed transition-colors';
  return (
    <div className="flex items-center justify-between py-3">
      <div>
        <p className="text-sm font-semibold text-slate-900">{label}</p>
        <p className="text-xs text-slate-500">{sub}</p>
      </div>
      <div className="flex items-center gap-3">
        <button type="button" aria-label={`Fewer ${label}`} className={btn} disabled={value <= min} onClick={() => onChange(value - 1)}>−</button>
        <span className="w-4 text-center text-sm font-bold text-slate-900">{value}</span>
        <button type="button" aria-label={`More ${label}`} className={btn} disabled={value >= max} onClick={() => onChange(value + 1)}>+</button>
      </div>
    </div>
  );
}

function TravelersPicker({ pax, setPax, cabin, setCabin }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const total = pax.adults + pax.children + pax.infants;
  const cabinLabel = CABINS.find(c => c.value === cabin)?.label;

  useEffect(() => {
    const close = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, []);

  const set = (field) => (v) => setPax(p => {
    const next = { ...p, [field]: v };
    if (next.infants > next.adults) next.infants = next.adults;
    return next;
  });

  return (
    <div ref={ref} className="relative">
      <button type="button" onClick={() => setOpen(o => !o)} className="w-full flex items-center text-left outline-none" aria-expanded={open}>
        {Icon.user}
        <span className="text-base font-semibold text-slate-900 truncate">
          {total} traveler{total !== 1 ? 's' : ''}<span className="text-slate-500 font-normal">, {cabinLabel}</span>
        </span>
      </button>

      {open && (
        <div className="absolute z-50 right-0 lg:-right-4 mt-4 w-[300px] max-w-[calc(100vw-3rem)] bg-white border border-slate-200 rounded-2xl shadow-2xl p-5">
          <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">Cabin</p>
          <div className="grid grid-cols-3 gap-1 bg-slate-100 p-1 rounded-xl mb-3">
            {CABINS.map(c => (
              <button key={c.value} type="button" onClick={() => setCabin(c.value)}
                className={`py-2 rounded-lg text-sm font-semibold transition-all ${cabin === c.value ? 'bg-white text-blue-700 shadow-sm' : 'text-slate-600 hover:text-slate-900'}`}>
                {c.label}
              </button>
            ))}
          </div>
          <div className="divide-y divide-slate-100">
            <Counter label="Adults" sub="Age 12+" value={pax.adults} min={1} max={9} onChange={set('adults')} />
            <Counter label="Children" sub="Age 2–11" value={pax.children} min={0} max={8} onChange={set('children')} />
            <Counter label="Infants" sub="Under 2, on lap" value={pax.infants} min={0} max={pax.adults} onChange={set('infants')} />
          </div>
          <button type="button" onClick={() => setOpen(false)}
            className="mt-3 w-full bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold py-2.5 rounded-xl transition-colors">
            Done
          </button>
        </div>
      )}
    </div>
  );
}

export default function HomeSearchForm() {
  const navigate = useNavigate();
  const today = new Date().toISOString().split('T')[0];

  const [tripType, setTripType] = useState('roundtrip');
  const [origin, setOrigin] = useState('');
  const [destination, setDestination] = useState('');
  const [date, setDate] = useState('');
  const [returnDate, setReturnDate] = useState('');
  const [cabin, setCabin] = useState('economy');
  const [pax, setPax] = useState({ adults: 1, children: 0, infants: 0 });
  const [error, setError] = useState('');

  const swap = () => { setOrigin(destination); setDestination(origin); };

  const submit = (e) => {
    e.preventDefault();
    if (!origin || !destination) return setError('Choose where you are flying from and to.');
    if (origin === destination) return setError('Origin and destination cannot be the same.');
    if (!date) return setError('Choose a departure date.');
    if (tripType === 'roundtrip' && !returnDate) return setError('Choose a return date, or switch to one way.');
    if (tripType === 'roundtrip' && returnDate < date) return setError('Return date must be after departure date.');
    setError('');
    const params = new URLSearchParams({
      origin, destination, date,
      ...(tripType === 'roundtrip' ? { returnDate } : {}),
      adults: pax.adults, children: pax.children, infants: pax.infants,
      passengers: pax.adults + pax.children + pax.infants,
      class: cabin, tripType,
    }).toString();
    navigate(`/flights?${params}`);
  };

  const dateInput = 'w-full min-w-0 text-base font-semibold text-slate-900 bg-transparent outline-none [color-scheme:light]';

  return (
    <form onSubmit={submit} noValidate>
      <div className="flex items-center gap-1 mb-4" role="tablist" aria-label="Trip type">
        {[['roundtrip', 'Round trip'], ['oneway', 'One way']].map(([val, label]) => (
          <button key={val} type="button" role="tab" aria-selected={tripType === val} onClick={() => { setTripType(val); setError(''); }}
            className={`px-4 py-2 rounded-full text-sm font-semibold transition-colors ${tripType === val ? 'bg-blue-50 text-blue-700 ring-1 ring-blue-200' : 'text-slate-600 hover:bg-slate-100'}`}>
            {label}
          </button>
        ))}
      </div>

      <div className="flex flex-col lg:flex-row gap-2 lg:gap-0 lg:items-stretch">
        <div className="grid grid-cols-1 lg:flex lg:flex-1 gap-2 lg:gap-0 lg:border lg:border-slate-200 lg:rounded-2xl lg:divide-x lg:divide-slate-200 lg:bg-white">
          {/* From / swap / To */}
          <div className="relative grid grid-cols-1 lg:grid-cols-2 gap-2 lg:gap-0 lg:flex-[2] lg:divide-x lg:divide-slate-200">
            <Field label="From" className="lg:rounded-l-2xl">
              <AirportSearch variant="bare" name="origin" value={origin} onChange={(c) => { setOrigin(c); setError(''); }} placeholder="City or airport" icon={Icon.takeoff} />
            </Field>
            <Field label="To" className="lg:pl-7">
              <AirportSearch variant="bare" name="destination" value={destination} onChange={(c) => { setDestination(c); setError(''); }} placeholder="City or airport" icon={Icon.landing} />
            </Field>
            <button type="button" onClick={swap} aria-label="Swap origin and destination"
              className="absolute z-30 right-4 top-1/2 -translate-y-1/2 lg:right-auto lg:left-1/2 lg:-translate-x-1/2 w-9 h-9 rounded-full bg-white border border-slate-300 shadow-sm flex items-center justify-center text-slate-600 hover:text-blue-600 hover:border-blue-500 transition-colors">
              <svg className="w-4 h-4 shrink-0 rotate-90 lg:rotate-0" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M7 7h13m0 0-4-4m4 4-4 4M17 17H4m0 0 4 4m-4-4 4-4" />
              </svg>
            </button>
          </div>

          {/* Dates */}
          <div className={`grid gap-2 lg:gap-0 lg:flex-[1.9] lg:divide-x lg:divide-slate-200 ${tripType === 'roundtrip' ? 'grid-cols-2' : 'grid-cols-1'}`}>
            <Field label="Depart">
              <div className="flex items-center">
                <span className="hidden sm:inline lg:hidden">{Icon.calendar}</span>
                <input type="date" aria-label="Departure date" value={date} min={today} className={dateInput}
                  onChange={(e) => { setDate(e.target.value); if (returnDate && e.target.value > returnDate) setReturnDate(''); setError(''); }} />
              </div>
            </Field>
            {tripType === 'roundtrip' && (
              <Field label="Return">
                <div className="flex items-center">
                  <span className="hidden sm:inline lg:hidden">{Icon.calendar}</span>
                  <input type="date" aria-label="Return date" value={returnDate} min={date || today} className={dateInput}
                    onChange={(e) => { setReturnDate(e.target.value); setError(''); }} />
                </div>
              </Field>
            )}
          </div>

          {/* Travelers & cabin */}
          <Field label="Travelers & cabin" className="lg:flex-[1.25] lg:rounded-r-2xl">
            <TravelersPicker pax={pax} setPax={setPax} cabin={cabin} setCabin={setCabin} />
          </Field>
        </div>

        <button type="submit"
          className="lg:ml-3 flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 active:bg-blue-800 text-white font-bold text-base px-8 py-4 rounded-2xl shadow-lg shadow-blue-600/25 transition-colors">
          <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={2.2} viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-4.35-4.35M17 11A6 6 0 1 1 5 11a6 6 0 0 1 12 0z" />
          </svg>
          Search
        </button>
      </div>

      {error && (
        <p role="alert" className="mt-3 text-sm font-medium text-red-600 flex items-center gap-1.5">
          <span aria-hidden="true">!</span> {error}
        </p>
      )}
    </form>
  );
}
